import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
// mui
import {
  useTheme,
  PopoverOrigin,
  IconButton,
  Popover,
  Typography,
  CircularProgress,
} from "@mui/material";
// src
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import FormatUtil from "../../Utility/ProjectComUtil";
import {
  Data,
  EDataType,
} from "../../ProjectDetail/Contexts/CoordinateContext";
import {
  verticalShift,
  coordinateSystem as coordinateSystemData,
} from "../../../config/SelectListData";
import { Common } from "../../../../common/src";

type CustomPopoverProps = {
  anchorOrigin: PopoverOrigin;
  transformOrigin: PopoverOrigin;
  customColor: { color: string };
  localizedStorageData: Data[];
  styleColon: React.CSSProperties;
};

const CustomPopover = ({
  anchorOrigin,
  transformOrigin,
  customColor,
  localizedStorageData,
  styleColon,
}: CustomPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const stylePopKey: React.CSSProperties = {
    width: "120px",
    color: customColor.color,
    fontSize: "14px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  const stylePopValue: React.CSSProperties = {
    color: customColor.color,
    fontSize: "14px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  return (
    <span style={{ marginLeft: "10px" }}>
      <IconButton onClick={handleClick} style={{ padding: 0 }}>
        <FontAwesomeIcon icon={faCircleExclamation} style={customColor} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <Typography sx={{ p: 2 }}>
          {localizedStorageData.map((item) => {
            return (
              <div key={item.id} style={{ display: "flex" }}>
                <div style={stylePopKey}>{item.left}</div>
                <div style={styleColon}>:</div>
                <div style={stylePopValue}>
                  {Number(FormatUtil.removeDecimalPoint(Number(item.right), 5))}
                </div>
              </div>
            );
          })}
        </Typography>
      </Popover>
    </span>
  );
};

const CoordinateSystemJobSite = (props: any) => {
  const { vidocData, jobsiteName, isLoading } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const [storageData, setStorageData] = useState<Data[]>([
    { id: "horizontal", left: t("COORDINATE_SYSTEM"), right: "" },
    { id: "vertical", left: t("VERTICAL_COORDINATE_SYSTEM"), right: "" },
  ]);

  const [localizedStorageData, setLocalizedStorageData] = useState<Data[]>([
    { id: "rotation", left: t("ROTATION"), right: "" },
    { id: "originEasting", left: t("ORIGIN_EASTING"), right: "" },
    { id: "originNorthing", left: t("ORIGIN_NORTHING"), right: "" },
    { id: "originLatitude", left: t("ORIGIN_LATITUDE"), right: "" },
    { id: "originLongitude", left: t("ORIGIN_LONGITUDE"), right: "" },
    { id: "scaleFactor", left: t("SCALE_FACTOR"), right: "" },
    { id: "verticalOffset", left: t("VERTICAL_OFFSET"), right: "" },
    { id: "inclineX", left: t("INCLINE_X"), right: "" },
    { id: "inclineY", left: t("INCLINE_Y"), right: "" },
  ]);
  const [currentType, setCurrentType] = useState<EDataType>(EDataType.NONE);

  useEffect(() => {
    if (vidocData) {
      if (vidocData?.horizontal) {
        setCurrentType(EDataType.EPSG);
      } else if (vidocData?.originEasting) {
        setCurrentType(EDataType.LOCALIZE);
      } else {
        setCurrentType(EDataType?.NONE);
      }

      if (vidocData?.conditionFlag) {
        const newVidocData = localizedStorageData.map((item) => {
          item.right = vidocData[item.id as keyof Common.ProjectViDoc];
          return item;
        });
        setLocalizedStorageData(newVidocData);
      } else {
        const newlocalizeData = storageData.map((item) => {
          item.right = (
            item.id === "horizontal" ? coordinateSystemData : verticalShift
          ).find(
            (x) => x.value === vidocData[item.id as keyof Common.ProjectViDoc],
          )?.label;
          return item;
        });
        setStorageData(newlocalizeData);
      }
    } else {
      setCurrentType(EDataType?.NONE);
      setStorageData([
        { id: "horizontal", left: t("COORDINATE_SYSTEM"), right: "" },
        { id: "vertical", left: t("VERTICAL_COORDINATE_SYSTEM"), right: "" },
      ]);
    }
  }, [vidocData]);

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const styleColon: React.CSSProperties = {
    minHeight: "32px",
    fontSize: "16px",
    marginRight: "10px",
  };

  return (
    <div className="form-upload-coordinate-system-container">
      {currentType === EDataType.LOCALIZE &&
      localizedStorageData &&
      localizedStorageData.length > 0 ? (
        <div style={{ display: "flex" }}>
          <div style={{ color: customColor.color }} className="text title">
            {t("COORDINATE_SYSTEM")}
          </div>
          <div className="text">:</div>
          <div style={{ color: customColor.color }} className="text content">
            {t("COORDINATE_SYSTEM_JOBSITE")}({jobsiteName})
            <CustomPopover
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              customColor={customColor}
              styleColon={styleColon}
              localizedStorageData={localizedStorageData}
            />
          </div>
        </div>
      ) : (
        <>
          {storageData.map((item) => {
            return (
              <div key={item.id} style={{ display: "flex" }}>
                <div
                  style={{ color: customColor.color }}
                  className="text title"
                >
                  {item.left}
                </div>
                <div className="text">:</div>
                {isLoading ? (
                  <CircularProgress
                    size={15}
                    sx={{
                      marginTop: "5px",
                    }}
                  />
                ) : item.right !== "" &&
                  item.right !== null &&
                  item.right !== undefined ? (
                  <div
                    style={{ color: customColor.color }}
                    className="text content"
                  >
                    {item.right}
                  </div>
                ) : (
                  <div
                    style={{ color: customColor.color }}
                    className="text content"
                  >
                    {t("None")}
                  </div>
                )}
                {}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default CoordinateSystemJobSite;
